import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {OrganizationService} from '../../../shared/organization.service';
import {OrganizationCrmService, OrgOpportunity} from '../../../shared/organization-crm.service';
import {Organization} from '../../../interfaces/client';
import {OrganizationVacancy, PageMeta} from '../../../interfaces/organization-vacancy';

/**
 * One organisation: canonical name, the vacancies attributed to it, and this
 * Company's own CRM history against it.
 *
 * No alias, platform or raw-name detail is shown, and per the API guard the
 * backend does not send aliases to a Company that does not bridge the org.
 *
 * The opportunity timeline is fetched only when my_client is set:
 * forOrganization() takes a Client id and firstOrFail()s on parent_id, so
 * calling it for an untracked org would 404. The empty state stands in.
 */
@Component({
  selector: 'bst-organization-detail',
  templateUrl: './organization-detail.component.html',
  styleUrls: ['./organization-detail.component.scss'],
  standalone: false,
})
export class OrganizationDetailComponent implements OnInit {

  public org: Organization | null = null;
  public vacancies: OrganizationVacancy[] = [];
  public vacancyMeta: PageMeta | null = null;
  public opportunities: OrgOpportunity[] = [];
  public loading = false;
  public vacanciesUnavailable = false;
  public errorMessage: string | null = null;

  private orgId = 0;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private orgService: OrganizationService,
    private crm: OrganizationCrmService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.orgId = Number(this.route.snapshot.paramMap.get('id'));
    if (!this.orgId) {
      this.errorMessage = 'Missing organization id.';
      return;
    }
    this.loading = true;
    try {
      this.org = await this.orgService.fetch(this.orgId);
      await Promise.all([this.loadVacancies(1), this.loadOpportunities()]);
    } catch (e) {
      console.error('organization load failed', e);
      this.errorMessage = 'Failed to load this organization.';
    } finally {
      this.loading = false;
    }
  }

  public async loadVacancies(page: number): Promise<void> {
    let res;
    try {
      res = await this.orgService.vacancies(this.orgId, page);
    } catch (e) {
      // This endpoint's role gate is broader than the page's own, so a
      // failure here is a real failure rather than an expected state.
      console.error('organization vacancies load failed', e);
      this.vacanciesUnavailable = true;
      return;
    }
    this.vacanciesUnavailable = false;
    this.vacancies = res.data ?? [];
    this.vacancyMeta = res.meta ?? null;
  }

  private async loadOpportunities(): Promise<void> {
    const clientId = this.org?.my_client?.id;
    if (!clientId) { return; }
    // Non-fatal: the history is secondary to the org itself, so a failed
    // (already toasted) load degrades to an empty list instead of hiding the page.
    this.opportunities = await this.crm.listOpportunities(clientId).catch((e) => {
      console.error('organization opportunities load failed', e);
      return [];
    });
  }

  public get isTracked(): boolean {
    return !!this.org?.my_client;
  }

  /**
   * Translation key for the slot this org filled. Mapped here rather than
   * piping org_role straight into translate: org_role has no namespace in the
   * lang files, so its values only resolve as explicit flat keys.
   *
   * Opportunity status is the other way round and the template pipes it
   * directly, because crm.opp.* is a real nested namespace covering every value
   * of the backend enum. Both patterns are correct; which one applies depends on
   * whether the lang files namespace that particular field.
   */
  public roleKey(o: OrgOpportunity): string {
    switch (o.org_role) {
      case 'broker': return 'rolebroker';
      case 'both': return 'roleboth';
      default: return 'roleendclient';
    }
  }

  public openVacancy(v: OrganizationVacancy): void {
    // Carry where we came from, so the detail page's back control returns to
    // this organisation rather than to Market overview, which is where it lands
    // for every other caller. Passed as a URL rather than an organisation id so
    // the detail page needs no knowledge of who opened it.
    void this.router.navigate(['/market/vacancies', v.id], {
      queryParams: {from: this.router.url},
    });
  }

  public openOpportunity(o: OrgOpportunity): void {
    void this.router.navigate(['/opportunities', o.id]);
  }

  public back(): void {
    void this.router.navigate(['/organizations']);
  }
}
